import { PulseProduct } from 'shared/core/config/product.types';
import { pulseRoutes } from './pulseRoutes';

/**
 * Which product(s) can actually resolve a given dashboard id.
 *
 * The side menu lists every dashboard the user may reach, including ones whose
 * route lives under a different product (Revenue Mapping / trx-code-mapping is
 * registered in planRoutes but appears under Mapping Admin from Insights). The
 * menu used to build its links as `/products/<current product>/dashboard/<id>`,
 * which 404s for all of those.
 *
 * Derived from pulseRoutes rather than hand-listed, so adding or moving a route
 * needs no change here. pulseRoutes only references components through lazy
 * `loadComponent`, so importing it costs nothing at startup.
 */
const DASHBOARD_OWNERS: ReadonlyMap<string, PulseProduct[]> = (() => {
    const map = new Map<string, PulseProduct[]>();
    for (const entry of pulseRoutes) {
        const product = entry.path as PulseProduct;
        for (const child of entry.children ?? []) {
            const match = /^dashboard\/(.+)$/.exec(child.path ?? '');
            if (!match) continue;
            const id = match[1];
            const owners = map.get(id);
            if (owners) {
                owners.push(product);
            } else {
                map.set(id, [product]);
            }
        }
    }
    return map;
})();

/**
 * The product a dashboard link should point at.
 *
 * Prefers the product currently being viewed when that product can resolve the
 * dashboard (daily-digest is registered under both pro and insight, and should
 * stay where the user is). Otherwise returns the product that owns the route.
 * Unknown ids fall back to the current product, matching the old behaviour.
 */
export function productForDashboard(
    dashboardId: string,
    currentProduct: PulseProduct
): PulseProduct {
    const owners = DASHBOARD_OWNERS.get(dashboardId);
    if (!owners?.length) return currentProduct;
    return owners.includes(currentProduct) ? currentProduct : owners[0];
}
